import { isNull, location, product, sql, stock_picking, warehouse } from "@stockpilot/powersync";
import type { AnySQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CAPA LIVE CENTRALIZADA (choke point único)                  │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Este módulo es el ÚNICO lugar donde el invariante «fila viva = `deleted_at IS
 * NULL`» se expresa para las 4 tablas con papelera de este repo (verificadas
 * leyendo `packages/powersync/src/schema.ts`, no asumidas):
 *   product, warehouse, location, stock_picking.
 * Toda lectura viva de estas tablas compone estos predicados (los builders de
 * `inventory/queries.ts` y cualquier otra vista viva los importan de aquí). Las
 * ÚNICAS lecturas que ven filas borradas son la papelera
 * (`inventory/queries.ts#getTrashed*Query`, whitelisted como "trash") y algún
 * lookup id-scoped inofensivo o la resolución de nombres del feed de actividad
 * (whitelisted como "keyed"/"trash" en `guard-manifest.ts`).
 *
 * ¿Por qué centralizar? El invariante disperso ya causó bugs reales en este
 * repo (ver guard-manifest.ts): el contador de `checkUnsyncedActor` contaba
 * productos en papelera, la búsqueda ⌘K los mostraba en resultados, y
 * `getReceiptsQuery` (recepciones para landed costs) no excluía operaciones
 * archivadas.
 *
 * ─── Jerarquía de cohortes (inventario Stockpilot) ─────────────────────────
 * Verificada contra los FKs LÓGICOS de `packages/powersync/src/schema.ts` (no
 * hay FKs físicos en Postgres: son modelados lógicamente, igual que en
 * comandero/meal-planner):
 *   - product          → RAÍZ. `category_id`→product_category y `uom_id`→uom NO
 *                        son soft-delete, así que no hay cohorte de padre.
 *   - warehouse        → RAÍZ (solo `organization_id`).
 *   - location         → hijo de warehouse (`warehouse_id`). También tiene
 *                        `parent_id` (jerarquía interna, self-referencial),
 *                        pero ver el gotcha de PowerSync más abajo: NO se
 *                        implementa como cohorte de self-join.
 *   - stock_picking    → hijo de warehouse (`warehouse_id`) y de location, dos
 *                        veces (`src_location_id` origen, `dst_location_id`
 *                        destino) — ambos opcionales según el tipo de operación
 *                        (una recepción no tiene origen, p. ej.), por lo que el
 *                        NOT EXISTS resuelve a verdadero cuando el FK es NULL.
 *
 * NOTA (cohorte de un nivel, patrón del piloto/comandero/meal-planner): cada
 * cohorte comprueba el PADRE DIRECTO.
 *
 * GOTCHA DESCUBIERTO (PowerSync, no es un límite teórico — reventaba en
 * runtime): se intentó una cohorte de self-join para `location.parent_id` vía
 * `alias(location, "location_parent")` (drizzle). Compilaba y el SQL era
 * correcto para SQLite, pero el `resolveTables` de PowerSync (el mecanismo que
 * decide qué tablas observar para invalidar queries reactivas) escanea el SQL
 * en busca de nombres de tabla y NO entiende el alias — lo trata como una
 * tabla literal y explota en runtime: `Error: no such table: location_parent`
 * (visto en el navegador contra la BD local real, no en los tests `bun test`
 * con `drizzle-orm/sqlite-proxy`, que solo compilan a SQL y nunca ejecutan).
 * Esto tumbaba `getLocationsQuery` por completo (ninguna ubicación aparecía).
 * CONCLUSIÓN: en repos sobre PowerSync, las cohortes de self-join con alias no
 * son viables — se retiró. La cohorte de `location.parent_id` queda como
 * límite conocido (no implementado): una sub-ubicación de una ubicación en
 * papelera no se oculta por esa vía (solo por su propio `deleted_at` o por la
 * cohorte de `warehouse_id`). En la práctica la jerarquía de ubicaciones del
 * catálogo es plana (1-2 niveles) y el borde no se ha observado.
 */

/**
 * Predicado de cohorte genérico: la fila hija cuelga de un padre que NO está en
 * la papelera. Con soft-delete del padre los hijos persisten intactos (para
 * restaurar la cohorte entera) pero desaparecen de las vistas vivas. Si
 * `childFk` es NULL (FK opcional, p. ej. `src_location_id` de una recepción),
 * el NOT EXISTS resuelve a verdadero — no oculta la fila.
 */
function parentNotTrashed(
  parent: SQLiteTable & { id: AnySQLiteColumn; deleted_at: AnySQLiteColumn },
  childFk: AnySQLiteColumn,
) {
  return sql`NOT EXISTS (SELECT 1 FROM ${parent} WHERE ${parent.id} = ${childFk} AND ${parent.deleted_at} IS NOT NULL)`;
}

// ─── Raíces (sólo su propio deleted_at) ──────────────────────────────────────

/** Productos VIVOS (sin cohorte: category_id/uom_id no son soft-delete). */
export function liveProductFilters() {
  return [isNull(product.deleted_at)];
}

/** Almacenes VIVOS (raíz de la jerarquía de ubicaciones). */
export function liveWarehouseFilters() {
  return [isNull(warehouse.deleted_at)];
}

// ─── Hijos con cohorte (padre con papelera no borrado) ───────────────────────

/**
 * Ubicaciones VIVAS: su propio `deleted_at` + su almacén no está en papelera.
 * (NO incluye cohorte de `parent_id` — ver gotcha de PowerSync en el comentario
 * de módulo: un self-join con alias rompe `resolveTables` en runtime.)
 */
export function liveLocationFilters() {
  return [isNull(location.deleted_at), parentNotTrashed(warehouse, location.warehouse_id)];
}

/**
 * Operaciones (pickings) VIVAS: su propio `deleted_at` + su almacén y sus
 * ubicaciones de origen/destino (cuando existen) no están en papelera.
 */
export function liveStockPickingFilters() {
  return [
    isNull(stock_picking.deleted_at),
    parentNotTrashed(warehouse, stock_picking.warehouse_id),
    parentNotTrashed(location, stock_picking.src_location_id),
    parentNotTrashed(location, stock_picking.dst_location_id),
  ];
}
